import getConfig from "next/config";
import setCookieParser, { splitCookiesString } from "set-cookie-parser";
import { parse, serialize } from "cookie";
import jwt_decode from "jwt-decode";
import { ContextWithJaeger, ExtendedNextPageContext } from "types/app";
import {
  BeforeRequestInterceptor,
  GraphQLFetchClient,
} from "modules/graphql-fetch";
import { appendServerResponseCookie, getClientCookie } from "helpers/cookies";
import { minutesDiff } from "helpers/date";
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME } from "constants/variables";
import { startRequestMetrics, endRequestMetrics } from "./interceptors";
import { factory } from "./factory";

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();

export type GraphqlApi = IApi<typeof factory>;

type AuthToken = string | null;

export const createApi = (
  nextContext?: ContextWithJaeger<Pick<ExtendedNextPageContext, "req" | "res">>
): GraphqlApi => {
  let accessToken: AuthToken;
  let refreshToken: AuthToken;
  let lastErrorTraceId;
  const isServer = typeof window === "undefined";
  let refreshPromise = null;
  // todo: rework refreshing later
  let refreshing = false;
  let onRefreshToken;
  let onAuthError;

  const checkForRefresh = async () => {
    let diff = 0;

    if (refreshing) return refreshPromise;

    const refreshTokenCookie = getClientCookie(REFRESH_TOKEN_NAME) || "";
    const accessTokenCookie = getClientCookie(ACCESS_TOKEN_NAME) || "";

    if (refreshTokenCookie && refreshToken !== refreshTokenCookie) {
      accessToken = accessTokenCookie as string;
      refreshToken = refreshTokenCookie as string;

      onRefreshToken.call(this, accessTokenCookie, refreshTokenCookie);
    }

    if (accessToken) {
      try {
        const decodedToken: Record<string, string | number> =
          jwt_decode(accessToken);
        diff = minutesDiff((decodedToken?.exp as number) * 1000, new Date());
      } catch (err) {
        console.error("JWT decode error", err);
      }
    }

    if (diff < 1 && refreshToken) {
      accessToken = "";
      refreshing = true;

      refreshPromise = methods.auth.refreshToken({ refreshToken });

      try {
        const { data, errors } = await refreshPromise;

        if (data) {
          accessToken = data.refreshToken.accessToken;
          refreshToken = data.refreshToken.refreshToken;

          onRefreshToken.call(
            this,
            data.refreshToken.accessToken,
            data.refreshToken.refreshToken
          );
          refreshPromise = null;
        }

        if (errors) {
          console.error("[ApiService][refreshToken] graphql errors:", errors);
          refreshPromise = null;
          resetTokens();
          await onAuthError.call(this);
        }

        refreshing = false;
      } catch (err) {
        console.error("[ApiService][refreshToken] API error:", err);
        refreshing = false;
        resetTokens();
        await onAuthError.call(this);
      }
    }
  };

  const setRequestHeaders: BeforeRequestInterceptor = ({ headers }) => {
    if (accessToken && !refreshing) {
      headers.set("Authorization", accessToken ? `Bearer ${accessToken}` : "");
    }

    /* Add headers from next request */
    if (nextContext?.req?.headers) {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        host,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        connection,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        pragma,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        referer,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ["uber-trace-id"]: uberTraceId,
        ...restHeaders
      } = nextContext.req.headers;

      Object.entries(restHeaders).forEach(([key, value]) =>
        headers.append(key, value as string)
      );

      if (nextContext?.req.jaeger) {
        const span =
          nextContext.req.jaeger.currentSpan || nextContext.req.jaeger.rootSpan;
        headers.set("uber-trace-id", span.context().toString());
      }
    }
  };

  const setServerResponseCredentials = response => {
    //Next request: nextContext?.req
    //Next request cookie: nextContext?.req?.headers.cookie
    //Next response: nextContext?.res

    const responseHeaders = response.headers;

    if (responseHeaders) {
      //куки которые не нужно проксировать из bff
      const exceptions = [];
      const cookiesFromResponse =
        setCookieParser(
          splitCookiesString(responseHeaders.get("set-cookie"))
        ).filter(c => {
          return !exceptions.includes(c.name);
        }) || [];

      const cookiesFromResponseSerialize = cookiesFromResponse.map(cookie => {
        const { name, value, ...options } = cookie;
        return serialize(name, value, {
          ...options,
          path: options.path || "/",
          domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN
            ? publicRuntimeConfig.COOKIE_FORWARD_DOMAIN
            : options.domain,
        });
      });

      /* Set collected cookies to current next response */
      appendServerResponseCookie(nextContext, cookiesFromResponse);

      /* Set collected cookies in current next request context, for later graphql requests */
      if (nextContext?.req) {
        const parsedRequestCookieObject = nextContext.req.headers?.cookie
          ? parse(nextContext.req.headers.cookie)
          : {};

        if (cookiesFromResponseSerialize) {
          /* Override/add current cookie values with cookie values from graphql response */
          cookiesFromResponseSerialize.forEach(c => {
            const { name, value } = setCookieParser(c)[0] || {};

            if (name && value) {
              parsedRequestCookieObject[name] = value;
            }
          });

          const cookieStringForReplace = Object.keys(parsedRequestCookieObject)
            .map(key => serialize(key, parsedRequestCookieObject[key]))
            .join("; ");

          /* Replace cookie string in current request context, for using it in future graphql request */
          if (cookieStringForReplace) {
            nextContext.req.headers.cookie = cookieStringForReplace;
          }
        }
      }
    }

    return response;
  };

  const client = new GraphQLFetchClient({
    endpoint:
      serverRuntimeConfig.BFF_ENDPOINT_PRIVATE ||
      publicRuntimeConfig.BFF_ENDPOINT,
    credentials: publicRuntimeConfig.FETCH_CREDENTIALS || "same-origin",
    ssr: isServer,
  });

  client.beforeRequest(async context => {
    await checkForRefresh();

    setRequestHeaders(context);
    if (publicRuntimeConfig.LOG_METRICS) {
      startRequestMetrics(context);
    }
  });

  client.afterRequest((response, context) => {
    if (isServer) {
      setServerResponseCredentials(response);
    }

    if (publicRuntimeConfig.LOG_METRICS) {
      endRequestMetrics(context);
    }
  });

  client.onError(async (error, context, response) => {
    lastErrorTraceId = response?.headers.get("X-Trace-Id");

    if (error && [401, 403].includes(response.status)) {
      await onAuthError.call(this);
    }
  });

  const methods = factory(client);

  const setTokens = ({ access, refresh }) => {
    accessToken = access;
    refreshToken = refresh;
  };

  const resetTokens = () => {
    accessToken = "";
    refreshToken = "";
  };

  function afterRefreshRequest(interceptor) {
    if (typeof interceptor === "function") {
      onRefreshToken = interceptor;
    }
  }

  function onLogout(interceptor) {
    if (typeof interceptor === "function") {
      onAuthError = interceptor;
    }
  }

  const getLastErrorTraceId = () => {
    return lastErrorTraceId;
  };

  return {
    methods,
    getLastErrorTraceId,
    setTokens,
    resetTokens,
    onRefreshToken: afterRefreshRequest,
    onLogout,
  };
};
